import { Store } from 'antd/lib/form/interface';
import moment from 'moment';
import { isEmpty } from 'lodash';

import { TravelMethodId } from 'leash/utilities/const';
import { TravelEligibility, TravelMethodIdType } from 'leash/types';
import { DetailsActions } from 'leash/pages/details/useDetailsState';
import {
  BaseTravelProposal,
  CargoTravelProposal,
  ChaperoneTravelProposal,
  CharterTravelProposal,
  GroundTravelProposal,
  HubPassThruProposal,
  LocalDeliveryProposal,
  MeetupTravelProposal,
  Nullable,
  TravelPlan,
  TravelProposal,
} from 'types';
import date from 'utilities/dates/date';
import { apiPackage } from 'utilities/http/api/leash/apiPackage';
import { log } from 'utilities/log/log';
import { notify } from 'utilities/notifications/notify';

const sendProposalSaveError =
  'There’s an error while trying to save this proposal. Please contact Technical support.';
const sendProposalProposeError =
  'There’s an error while trying to send this proposal. Please contact technical support.';

export const populateFormOnLoad = (
  travelProposal: Nullable<TravelProposal>,
  travelMethodId: TravelMethodIdType,
  eligibilitySelected: TravelEligibility | undefined,
) => {
  const baseProposal = (p: TravelProposal) => ({
    departureTime: p?.departureTime ? moment(p.departureTime) : null,
    arrivalTime: p?.arrivalTime ? moment(p.arrivalTime) : null,
  });

  switch (travelMethodId) {
    case TravelMethodId.CharterFlight: {
      const charterProposal = travelProposal as CharterTravelProposal;
      return {
        ...baseProposal(charterProposal),
        crateSizeId: charterProposal?.crateSizeId,
        firstMileTransporterId: charterProposal?.firstMileTransporterCompanyId,
        lastMileTransporterId: charterProposal?.lastMileTransporterCompanyId,
        dropOffLocation: charterProposal?.dropOffLocation,
        pickupLocationId:
          charterProposal?.pickupLocationId || eligibilitySelected?.pickupLocation?.id,
        originAirportId:
          charterProposal?.originAirportId || eligibilitySelected?.originHubAirport?.id,
        destinationAirportId:
          charterProposal?.destinationAirportId ||
          eligibilitySelected?.destinationHubAirport?.id,
      };
    }
    case TravelMethodId.CommercialCargo: {
      const cargoProposal = travelProposal as CargoTravelProposal;
      return {
        ...baseProposal(cargoProposal),
        crateSizeId: cargoProposal?.crateSizeId,
        airlineId: cargoProposal?.airlineId,
        originAirportId: cargoProposal?.originAirportId,
        destinationAirportId:
          cargoProposal?.destinationAirportId || eligibilitySelected?.airport?.id,
        dropOffLocation: cargoProposal?.dropOffLocation,
        firstMileTransporterId: cargoProposal?.firstMileTransporterCompanyId,
        pickupLocationId: cargoProposal?.pickupLocationId,
      };
    }
    case TravelMethodId.Chaperone: {
      const chaperoneProposal = travelProposal as ChaperoneTravelProposal;
      return {
        ...baseProposal(chaperoneProposal),
        puppyChaperoneCompanyId: chaperoneProposal?.puppyChaperoneCompanyId,
        originAirportId: chaperoneProposal?.originAirportId,
        destinationAirportId: chaperoneProposal?.destinationAirportId,
        dropOffLocation: chaperoneProposal?.dropOffLocation,
      };
    }
    case TravelMethodId.GroundTransportation: {
      const groundTransporter = travelProposal as GroundTravelProposal;
      return {
        ...baseProposal(groundTransporter),
        groundTransporter: groundTransporter?.groundTransportationCompanyId,
        pickupLocationId: groundTransporter?.pickupLocationId,
        groundTransportationCompanyId: groundTransporter?.groundTransportationCompanyId,
      };
    }
    case TravelMethodId.LocalDelivery: {
      const localDeliveryTransporter = travelProposal as LocalDeliveryProposal;
      return {
        ...baseProposal(localDeliveryTransporter),
        transporterCompany: localDeliveryTransporter?.transporterCompanyId,
      };
    }
    case TravelMethodId.HubPassThru: {
      const hubPassThruProposal = travelProposal as HubPassThruProposal;
      return {
        ...baseProposal(hubPassThruProposal),
        crateSizeId: hubPassThruProposal?.crateSizeId,
        firstMileTransporterId: hubPassThruProposal?.firstMileTransporterCompanyId,
        lastMileTransporterId: hubPassThruProposal?.lastMileTransporterCompanyId,
        dropOffLocation: hubPassThruProposal?.dropOffLocation,
        hubAirportId: hubPassThruProposal?.hubAirportId,
      };
    }
    case TravelMethodId.Meetup:
    default: {
      const meetupProposal = travelProposal as MeetupTravelProposal;
      return {
        ...baseProposal(meetupProposal),
        meetupLocation: meetupProposal?.meetupLocation,
        arrivalTimeOfDay: meetupProposal?.arrivalTime
          ? moment(meetupProposal.arrivalTime)
          : null,
      };
    }
  }
};

export const buildProposalPayload = (
  e: Store,
  travelMethodId: TravelMethodIdType,
): TravelProposal => {
  const baseProposal: BaseTravelProposal = {
    departureTime: e?.departureTime ? date.toISO(e.departureTime) : null,
    arrivalTime: e?.arrivalTime ? date.toISO(e.arrivalTime) : null,
  };

  switch (travelMethodId) {
    case TravelMethodId.CharterFlight: {
      const charterProposal: CharterTravelProposal = {
        departureTime: e?.arrivalTime ? date.toISO(e.arrivalTime) : null,
        arrivalTime: e?.arrivalTime ? date.toISO(e.arrivalTime) : null,
        crateSizeId: e.crateSizeId ?? null,
        firstMileTransporterCompanyId: e.firstMileTransporterId ?? null,
        lastMileTransporterCompanyId: e.lastMileTransporterId ?? null,
        dropOffLocation: e.dropOffLocation,
        pickupLocationId: e.pickupLocationId ?? null,
        originAirportId: e.originAirportId ?? null,
        destinationAirportId: e.destinationAirportId ?? null,
      };
      return charterProposal;
    }
    case TravelMethodId.CommercialCargo: {
      const cargoProposal: CargoTravelProposal = {
        ...baseProposal,
        crateSizeId: e.crateSizeId ?? null,
        airlineId: e.airlineId ?? null,
        originAirportId: e.originAirportId ?? null,
        destinationAirportId: e.destinationAirportId ?? null,
        dropOffLocation: e.dropOffLocation ?? null,
        firstMileTransporterCompanyId: e.firstMileTransporterId ?? null,
        pickupLocationId: e.pickupLocationId ?? null,
      };
      return cargoProposal;
    }
    case TravelMethodId.Chaperone: {
      const chaperoneProposal: ChaperoneTravelProposal = {
        ...baseProposal,
        puppyChaperoneCompanyId: e.puppyChaperoneCompanyId ?? null,
        originAirportId: e.originAirportId ?? null,
        destinationAirportId: e.destinationAirportId ?? null,
        dropOffLocation: e.dropOffLocation ?? null,
      };
      return chaperoneProposal;
    }
    case TravelMethodId.GroundTransportation: {
      const transportation: GroundTravelProposal = {
        ...baseProposal,
        groundTransportationCompanyId:
          e.groundTransporter || e.groundTransportationCompanyId || null,
        pickupLocationId: e.pickupLocationId ?? null,
      };
      return transportation;
    }
    case TravelMethodId.LocalDelivery: {
      const localDeliveryProposal: LocalDeliveryProposal = {
        ...baseProposal,
        arrivalTime: e?.departureTime ? date.toISO(e.departureTime) : null,
        transporterCompanyId: e.transporterCompany ?? null,
      };
      return localDeliveryProposal;
    }
    case TravelMethodId.HubPassThru: {
      const hubPassThruProposal: HubPassThruProposal = {
        ...baseProposal,
        arrivalTime: e?.departureTime ? date.toISO(e.departureTime) : null,
        crateSizeId: e.crateSizeId ?? null,
        firstMileTransporterCompanyId: e.firstMileTransporterId ?? null,
        lastMileTransporterCompanyId: e.lastMileTransporterId ?? null,
        dropOffLocation: e.dropOffLocation,
        hubAirportId: e.hubAirportId ?? null,
      };
      return hubPassThruProposal;
    }
    case TravelMethodId.Meetup:
    default: {
      const meetupProposal: MeetupTravelProposal = {
        ...baseProposal,
        arrivalTime: getMeetupArrivalDateWithTime(e) ?? null,
        meetupLocation: isEmpty(e.meetupLocation) ? null : e.meetupLocation,
      };
      return meetupProposal;
    }
  }
};

export const handleOnSubmit = async (
  values: Store,
  travelPlan: TravelPlan,
  dispatch: (action: DetailsActions) => void,
) => {
  const receivedProposal = await onSubmitProposal(values, travelPlan);
  if (receivedProposal) {
    dispatch({ type: 'SET_TRAVEL_PROPOSAL', travelProposal: receivedProposal });
  }
};

export const onSubmitProposal = async (
  e: Store,
  travelPlan: TravelPlan,
): Promise<TravelProposal | null> => {
  const { travelPackage } = travelPlan;
  const { travelMethodId } = travelPackage;

  const proposalPayload = buildProposalPayload(e, travelMethodId);
  try {
    let response;
    if (travelPackage?.travelProposal) {
      const proposalId = travelPackage.travelProposal?.id ?? 0;
      response = await apiPackage.updateTravelProposal(
        travelPackage.id,
        proposalId,
        proposalPayload,
      );
    } else {
      response = await apiPackage.createTravelProposal(travelPackage.id, proposalPayload);
    }

    if (!response.isOk) {
      notify.error('Error saving the travel proposal');
      return null;
    } else {
      notify.success('Estimated departure date saved', 'travel-proposal-saved');
      return response.json.travelProposal;
    }
  } catch (updateProposalError) {
    log.debug('updateProposalError', updateProposalError);
    notify.error('Error saving the travel proposal');
    return null;
  }
};

export const onSendProposal = async (e: Store, travelPlan: TravelPlan) => {
  const { travelPackage } = travelPlan;
  const { travelMethodId } = travelPackage;

  const proposalPayload = buildProposalPayload(e, travelMethodId);

  try {
    let response;
    if (travelPackage?.travelProposal) {
      const proposalId = travelPackage.travelProposal?.id ?? 0;
      response = await apiPackage.updateTravelProposal(
        travelPackage.id,
        proposalId,
        proposalPayload,
      );
    } else {
      response = await apiPackage.createTravelProposal(travelPackage.id, proposalPayload);
    }
    if (response.isOk) {
      await handleSendProposeAfterSave(travelPackage.id, proposalPayload);
    } else {
      notify.error(sendProposalSaveError);
    }
  } catch (e) {
    log.debug(e);
    notify.error(sendProposalSaveError);
  }
};

export const getMeetupArrivalDateWithTime = (e: Store) => {
  const { arrivalTime, arrivalTimeOfDay } = e;
  if (!arrivalTime) return null;

  const dateISO = date.toISO(arrivalTime);
  const time = arrivalTimeOfDay ? moment(arrivalTimeOfDay).format('HH:mm') : '00:00';
  return `${dateISO} ${time}:00`;
};

const handleSendProposeAfterSave = async (
  travelPackageId: number,
  proposalPayload: TravelProposal,
) => {
  try {
    const response = await apiPackage.proposeTravelProposal(
      travelPackageId,
      proposalPayload,
    );
    if (!response.isOk) {
      notify.error(sendProposalProposeError);
    } else {
      notify.success('Travel proposal sent', 'travel-proposal-sent');
    }
  } catch (e) {
    log.debug(e);
    notify.error(sendProposalSaveError);
  }
};
